import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Globe,
  Link2,
  Palette,
  Search,
  List,
  Loader2,
  ExternalLink,
  Copy,
  Check,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import PlanUpgradeOverlay from '../plan/PlanUpgradeOverlay';
import { API_URL } from '../../config';

const PublishingSettings = ({ spaceId, spaceName, orgId, hasCustomBranding = true }) => {
  const { currentOrg } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const canManage = currentOrg?.user_role === 'company';

  const fetchSettings = useCallback(async () => {
    if (!spaceId || !orgId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/publish`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch publish settings:', error);
      toast.error('Failed to load publishing settings');
    } finally {
      setLoading(false);
    }
  }, [spaceId, orgId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/organizations/${orgId}/spaces/${spaceId}/publish`,
        settings
      );
      setSettings(response.data);
      toast.success('Publishing settings saved');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (checked) => {
    const newSettings = { ...settings, is_published: checked };
    setSettings(newSettings);
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/organizations/${orgId}/spaces/${spaceId}/publish`,
        { is_published: checked }
      );
      setSettings(prev => ({ ...prev, ...response.data }));
      toast.success(checked ? 'Space published!' : 'Space unpublished');
    } catch (error) {
      setSettings(prev => ({ ...prev, is_published: !checked }));
      toast.error('Failed to update publishing status');
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(settings.preview_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('URL copied to clipboard');
  };

  if (loading) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="py-8 text-center">
          <p className="text-slate-500">Failed to load settings</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="publishing-settings">
      {/* Publish Toggle */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                  Publish Documentation
                </CardTitle>
                <CardDescription>
                  Make this space publicly accessible
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.is_published}
              onCheckedChange={handleTogglePublish}
              disabled={saving || !canManage}
              data-testid="publish-toggle"
            />
          </div>
        </CardHeader>
        {settings.is_published && (
          <CardContent className="pt-0">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300 mb-1">
                    Your documentation is live!
                  </p>
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 truncate font-mono">
                    {settings.preview_url}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyUrl}
                    className="border-emerald-300 dark:border-emerald-700"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(settings.preview_url, '_blank')}
                    className="border-emerald-300 dark:border-emerald-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* URL Settings */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <Link2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                URL Settings
              </CardTitle>
              <CardDescription>
                Configure your documentation URL
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="preview_path">Preview Path</Label>
            <div className="flex gap-2">
              <span className="flex items-center px-3 bg-slate-100 dark:bg-slate-800 text-slate-500 text-sm rounded-l-md border border-r-0 border-slate-200 dark:border-slate-700">
                /docs/
              </span>
              <Input
                id="preview_path"
                value={spaceId || ''}
                readOnly
                className="rounded-l-none font-mono"
                data-testid="publish-preview-path-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              Local preview links now use the space ID instead of the space name or slug.
            </p>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="custom_domain">Custom Domain (Optional)</Label>
            <Input
              id="custom_domain"
              value={settings.custom_domain || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, custom_domain: e.target.value }))}
              placeholder="docs.yourdomain.com"
              disabled={!canManage}
              data-testid="publish-custom-domain-input"
            />
            <p className="text-xs text-slate-500">
              For a branded docs URL (e.g. <span className="font-mono">docs.yourdomain.com</span>), use{' '}
              <strong>Settings → Domain</strong> for this space. There you will see the exact CNAME target
              for your deployment.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Appearance — Growth+ (custom branding) */}
      <div className="relative rounded-xl">
        <Card
          className={`bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 ${
            !hasCustomBranding ? 'pointer-events-none opacity-60' : ''
          }`}
          data-testid="publishing-appearance-card"
        >
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                  Appearance
                </CardTitle>
                <CardDescription>
                  Customize the look of your documentation
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="theme">Theme</Label>
                <Select
                  value={settings.theme || 'default'}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, theme: value }))}
                  disabled={!canManage}
                >
                  <SelectTrigger id="theme">
                    <SelectValue placeholder="Select theme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                    <SelectItem value="auto">Auto (System)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="primary_color">Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="primary_color"
                    type="color"
                    value={settings.primary_color || '#2563eb'}
                    onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-12 h-10 p-1 cursor-pointer"
                    disabled={!canManage}
                  />
                  <Input
                    value={settings.primary_color || '#2563eb'}
                    onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="flex-1 font-mono"
                    disabled={!canManage}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                value={settings.logo_url || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, logo_url: e.target.value }))}
                placeholder="https://example.com/logo.png"
                disabled={!canManage}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_dark_url">Logo URL (dark mode)</Label>
              <Input
                id="logo_dark_url"
                value={settings.logo_dark_url || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, logo_dark_url: e.target.value }))}
                placeholder="https://example.com/logo-dark.png"
                disabled={!canManage}
              />
              <p className="text-xs text-slate-500">
                Optional — the light logo is used in both themes when this is empty.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="footer_text">Footer Text</Label>
              <Input
                id="footer_text"
                value={settings.footer_text || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, footer_text: e.target.value }))}
                placeholder="© 2026 Your Company. All rights reserved."
                disabled={!canManage}
              />
            </div>
          </CardContent>
        </Card>
        {!hasCustomBranding && (
          <PlanUpgradeOverlay
            title="Custom publish branding"
            description="Theme, colors, logo, and footer for public docs are available on Growth and Corporate plans."
          />
        )}
      </div>

      {/* Features */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
              <Settings2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                Features
              </CardTitle>
              <CardDescription>
                Toggle documentation features
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5 text-slate-400" />
              <div>
                <Label>Search</Label>
                <p className="text-xs text-slate-500">Allow visitors to search documentation</p>
              </div>
            </div>
            <Switch
              checked={settings.show_search !== false}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_search: checked }))}
              disabled={!canManage}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <List className="w-5 h-5 text-slate-400" />
              <div>
                <Label>Table of Contents</Label>
                <p className="text-xs text-slate-500">Show page outline on the right sidebar</p>
              </div>
            </div>
            <Switch
              checked={settings.show_toc !== false}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_toc: checked }))}
              disabled={!canManage}
            />
          </div>
        </CardContent>
      </Card>

      {/* SEO */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
            SEO Settings
          </CardTitle>
          <CardDescription>
            Optimize your documentation for search engines
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="meta_title">Meta Title</Label>
            <Input
              id="meta_title"
              value={settings.meta_title || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, meta_title: e.target.value }))}
              placeholder={spaceName}
              disabled={!canManage}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="meta_description">Meta Description</Label>
            <Input
              id="meta_description"
              value={settings.meta_description || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, meta_description: e.target.value }))}
              placeholder="Documentation for..."
              disabled={!canManage}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      {canManage && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="publish-save-button"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PublishingSettings;
